import { useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import ReviewProfileReference from "./ReviewProfileReference.jsx";
import { incidentTone, SEVERITY_COLOR } from "../lib/traceReview.js";
import { turnText } from "../lib/traceEvaluation.js";
import { placeReviewCitations } from "../lib/traceReviewFindings.js";

const FINDING_META = "text-[10px] leading-[1.4] text-[color:var(--muted)]";

function claimOf(claim) {
    const result = String(claim?.result || "").toLowerCase();
    if (result === "upheld") return { holds: true, reasoning: claim.reasoning || null };
    if (result === "refuted") return { holds: false, reasoning: claim.reasoning || null };
    return null;
}

export function adaptBackendFindingToLanes(finding, turns = []) {
    const turnInputs = turns.map((turn) => ({
        messageId: turn?.messageId || null,
        text: turnText(turn),
    }));
    const citations = placeReviewCitations((finding.citations || []).map((citation) => ({
        ...citation,
        messageId: citation?.source?.messageId || null,
    })), turnInputs).map((citation) => ({
        ...citation,
        verified: citation.locatedTurn == null ? "missing" : citation.status || "exact",
    }));
    const placed = citations.filter((citation) => citation.locatedTurn != null);
    return {
        id: finding.findingId || null,
        reportId: finding.reportId || null,
        title: finding.title || "(untitled finding)",
        rationale: finding.rationale || null,
        reason: finding.reason || null,
        evidenceStatus: finding.evidenceStatus || null,
        verifierStatus: finding.verifierStatus || null,
        behaviour: finding.behaviourSeverity ? {
            severity: finding.behaviourSeverity,
            category: finding.behaviourCategory || null,
            definition: finding.behaviourDefinition || null,
        } : null,
        content: finding.contentSensitivity ? {
            sensitivity: finding.contentSensitivity,
            category: finding.contentCategory || null,
            definition: finding.contentDefinition || null,
        } : null,
        policy: finding.policy?.status ? { status: finding.policy.status, clause: finding.policy.reference || null, note: finding.policy.note || null } : null,
        norms: finding.norms?.status ? { status: finding.norms.status, norm: finding.norms.reference || null, note: finding.norms.note || null } : null,
        intent: finding.intent?.status ? { status: finding.intent.status, intent: finding.intent.reference || null, note: finding.intent.note || null } : null,
        policyClaim: claimOf(finding.policy?.claim),
        intentClaim: claimOf(finding.intent?.claim),
        normsClaim: claimOf(finding.norms?.claim),
        citations,
        citedCount: placed.length,
        citationCount: citations.length,
        uncited: placed.length === 0,
        turns: [...new Set(placed.map((citation) => citation.locatedTurn))].sort((a, b) => a - b),
    };
}

function citationContext(citation, turns) {
    if (!citation.locatedTurn) return null;
    const text = turnText(turns[citation.locatedTurn - 1]);
    if (!text) return null;
    const needle = String(citation.quote || "").replace(/^["'“”…\s]+/, "").replace(/["'“”…\s]+$/, "");
    const at = needle ? text.indexOf(needle) : -1;
    if (at < 0) return { before: text.slice(0, 1600), match: "", after: "" };
    const from = Math.max(0, at - 700);
    const to = Math.min(text.length, at + needle.length + 900);
    return {
        before: `${from > 0 ? "..." : ""}${text.slice(from, at)}`,
        match: text.slice(at, at + needle.length),
        after: `${text.slice(at + needle.length, to)}${to < text.length ? "..." : ""}`,
    };
}

function Citation({ citation, turns, onNavTurn, turnLabelBase }) {
    const missing = citation.verified === "missing";
    const turnLabel = citation.locatedTurn == null ? null : turnLabelBase + citation.locatedTurn - 1;
    const cls = `inline-flex max-w-full min-w-0 items-baseline gap-[5px] rounded-[2px] border px-[6px] py-[2px] text-[11px] leading-[1.2] ${
        missing
            ? "border-[color:var(--warn,#c08a2d)] text-[color:var(--muted)]"
            : "cursor-pointer border-[color:var(--line)] bg-[color:var(--bg)] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
    }`;
    const body = (
        <>
            <span className="shrink-0 text-[10px] tracking-[0.05em] text-[color:var(--muted)]">
                {missing ? (citation.unloaded ? "EARLIER" : "NOT LOCATED") : `T${turnLabel}`}
            </span>
            <span className="min-w-0 truncate">"{citation.quote}"</span>
        </>
    );
    const chip = missing || !onNavTurn
        ? <span className={cls}>{body}</span>
        : <button type="button" data-finding-cite="" className={cls} onClick={() => onNavTurn(citation.locatedTurn - 1)}>{body}</button>;
    const context = citationContext(citation, turns);
    if (!context) return chip;
    return (
        <HoverTooltip
            wide
            maxWidth={560}
            placement="top"
            label={(
                <span className="grid gap-[5px] text-left">
                    <span className="text-[10px] uppercase tracking-[0.05em] text-[color:var(--muted)]">Turn {turnLabel}</span>
                    <span className="block max-h-[300px] overflow-hidden whitespace-pre-wrap break-words font-mono text-[10px] leading-[1.45] text-[color:var(--muted)]">
                        {context.before}
                        {context.match ? <span className="text-[color:var(--ink)]" style={{ backgroundColor: "color-mix(in srgb, var(--amber,#b8860b) 22%, transparent)", boxShadow: "inset 0 -2px 0 var(--amber,#b8860b)" }}>{context.match}</span> : null}
                        {context.after}
                    </span>
                </span>
            )}
        >
            {chip}
        </HoverTooltip>
    );
}

export default function TraceReviewFindingCard({
    inc,
    profile = null,
    incident = null,
    onNavTurn = null,
    turns = [],
    collapsible = false,
    expanded = null,
    onExpandedChange = null,
    turnLabelBase = 1,
}) {
    const [localOpen, setLocalOpen] = useState(!collapsible);
    const open = expanded == null ? localOpen : expanded;
    const toggleOpen = () => {
        const next = !open;
        if (expanded == null) setLocalOpen(next);
        onExpandedChange?.(next);
    };
    const tone = incidentTone(inc);
    const color = SEVERITY_COLOR[tone];
    const rungs = [
        inc.behaviour?.severity && inc.behaviour.severity !== "none" ? `behaviour ${inc.behaviour.severity}` : null,
        inc.content?.sensitivity && inc.content.sensitivity !== "none" ? `content ${inc.content.sensitivity}` : null,
    ].filter(Boolean);
    const frames = [
        inc.policy ? { name: "policy", kind: "policy", status: inc.policy.status, ref: inc.policy.clause, claim: inc.policyClaim } : null,
        inc.norms ? { name: "norms", kind: "norms", status: inc.norms.status, ref: inc.norms.norm, claim: null } : null,
        inc.intent ? { name: "intent", kind: "intent", status: inc.intent.status, ref: inc.intent.intent, claim: inc.intentClaim } : null,
    ].filter((frame) => frame && frame.status && frame.status !== "no_view");
    const content = (
        <>
            <span className={`flex min-w-0 flex-wrap items-baseline gap-x-[8px] ${FINDING_META}`}>
                {rungs.map((rung) => <span key={rung} style={{ color }}>{rung}</span>)}
                {inc.behaviour?.category ? <span>{inc.behaviour.category}</span> : null}
                {inc.content?.category && inc.content.category !== inc.behaviour?.category ? <span>{inc.content.category}</span> : null}
                {inc.reason ? <span>{inc.reason.replace(/_/g, " ")}</span> : null}
            </span>
            {frames.length ? (
                <span className={`grid min-w-0 gap-[2px] ${FINDING_META}`}>
                    {frames.map((frame) => (
                        <span key={frame.name} className="flex min-w-0 flex-wrap items-baseline gap-x-[6px] text-left">
                            <span className="uppercase tracking-[0.05em]">{frame.name}</span>
                            <span className="text-[color:var(--ink)]">{frame.status}</span>
                            {frame.ref ? <ReviewProfileReference profile={profile} kind={frame.kind} reference={frame.ref} className="min-w-0" alignDefinitionToRow /> : null}
                            {frame.claim ? (
                                <span style={{ color: frame.claim.holds ? "var(--green)" : SEVERITY_COLOR.warn }}>
                                    {frame.claim.holds ? "claim upheld" : "claim refuted"}
                                </span>
                            ) : null}
                        </span>
                    ))}
                </span>
            ) : null}
            {inc.rationale ? <span className="text-[11px] leading-[1.45] text-[color:var(--muted)] [overflow-wrap:anywhere]">{inc.rationale}</span> : null}
            <span className="flex min-w-0 flex-wrap gap-[4px]">
                {inc.citations.map((citation, index) => <Citation key={index} citation={citation} turns={turns} onNavTurn={onNavTurn} turnLabelBase={turnLabelBase} />)}
            </span>
        </>
    );
    return (
        <div
            data-dock-finding={inc.id || ""}
            className="grid min-w-0 gap-[4px] border-l-2 pl-[8px]"
            style={{ borderColor: `color-mix(in srgb, ${color} 55%, transparent)` }}
        >
            {collapsible ? (
                <button type="button" aria-expanded={open} onClick={toggleOpen} className="flex min-w-0 cursor-pointer items-baseline gap-[7px] border-0 bg-transparent p-0 text-left text-[11px] leading-[1.4] text-[color:var(--ink)]">
                    <span className="shrink-0 text-[color:var(--muted-soft)]">{open ? "\u25be" : "\u25b8"}</span>
                    <span className="min-w-0 [overflow-wrap:anywhere]">{inc.title}</span>
                    {inc.evidenceStatus === "partial" ? <span className={`${FINDING_META} shrink-0`}>partial evidence</span> : null}
                    {incident && incident.count > 1 ? <span className={`${FINDING_META} shrink-0`} data-finding-recurs="">{incident.count} occurrences {"\u00b7"} T{turnLabelBase + incident.from}{"\u2013"}T{turnLabelBase + incident.through}</span> : null}
                </button>
            ) : (
                <span className="flex min-w-0 items-baseline gap-[7px] text-[11px] leading-[1.4] text-[color:var(--ink)]">
                    <span className="min-w-0 [overflow-wrap:anywhere]">{inc.title}</span>
                    {inc.evidenceStatus === "partial" ? <span className={`${FINDING_META} shrink-0`}>partial evidence</span> : null}
                    {incident && incident.count > 1 ? <span className={`${FINDING_META} shrink-0`} data-finding-recurs="">{incident.count} occurrences {"\u00b7"} T{turnLabelBase + incident.from}{"\u2013"}T{turnLabelBase + incident.through}</span> : null}
                </span>
            )}
            {open ? content : null}
        </div>
    );
}
